<template>
  <div class="chart-context-menu">
    <context-menu v-model:show="internalShow" :options="menuOptions">
      <!-- controls -->
      <!-- TODO: menu also configuratble from json object/arr -->
      <context-menu-item
        v-for="(item, index) in menuItems"
        :key="index"
        :icon-font-class="item.icon"
        @click="item.action"
      >
        <div class="context-menu-item-content">
          <font-awesome-icon :icon="item.icon" :title="item.label" />
          <label>{{ item.label }}</label>
          <span v-if="item.shortcut">{{ item.shortcut }}</span>
        </div>
      </context-menu-item>
    </context-menu>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { ContextMenu, ContextMenuItem } from '@imengyu/vue3-context-menu';
import { useI18n } from 'vue-i18n';
import { mapGetters } from 'vuex';
import { signalAmplitudeRules } from '../signal-graph/signalAmplitudeRules';
import { INVERTIBLE_SIGNAL_TITLES } from '../signal-graph/signal-graph.d3';
import type { ReorderableItem } from '@/common/ui';
import type { SignalPanel } from '@/common/types';

export default defineComponent({
  name: 'ChartContextMenu',
  components: {
    ContextMenu,
    ContextMenuItem
  },
  props: {
    menuOptions: {
      type: Object as PropType<any>,
      required: true
    },
    signalName: {
      type: String,
      required: true
    },
    show: {
      type: Boolean,
      required: true
    },
    actualDataMin: {
      type: Number as PropType<number | null>,
      default: null
    },
    actualDataMax: {
      type: Number as PropType<number | null>,
      default: null
    },
    pane: {
      type: String as PropType<SignalPanel | undefined>,
      required: false,
      default: undefined
    }
  },
  emits: ['update:show', 'invert-polarity'],
  computed: {
    ...mapGetters({
      epoch: 'epoch/getSignalMapping',
      scoreSet: 'scoreSet/getScoreSet'
    }),

    internalShow: {
      get() {
        return this.show;
      },
      set(value: boolean) {
        this.$emit('update:show', value);
      }
    },
    zoomRange() {
      const mapping = this.$store.getters['epoch/getSignalMapping'](this.signalName);
      return [mapping?.min, mapping?.max];
    },
    menuItems() {
      const { t } = useI18n();

      const menuItems = [
        {
          label: t('hideSignal'),
          icon: 'fa-solid fa-eye-slash',
          action: this.hideSignal
        },
        {
          label: t('increaseZoom'),
          icon: 'fa fa-plus',
          shortcut: '+',
          action: this.plusZoom
        },
        {
          label: t('decreaseZoom'),
          icon: 'fa fa-minus',
          shortcut: '-',
          action: this.minusZoom
        },
        {
          label: t('moveUp'),
          icon: 'fa fa-arrow-up',
          action: this.upZoom
        },
        {
          label: t('moveDown'),
          icon: 'fa fa-arrow-down',
          action: this.downZoom
        },
        {
          label: t('fitToPane'),
          icon: 'fa-minimize',
          action: this.fitToScreenZoom
        },
        {
          label: t('fitAllToPane'),
          icon: 'fa-solid fa-check-double',
          shortcut: 'f',
          action: this.fitAllToPane
        },
        {
          label: t('resetToDefault'),
          icon: 'fa-solid fa-rotate-left',
          shortcut: 'r',
          action: this.resetHoveredToDefault
        },
        {
          label: t('resetAllToDefault'),
          icon: 'fa-solid fa-arrows-rotate',
          shortcut: 'o',
          action: this.resetAllToDefault
        },
        {
          label: t('resetLaneHeights'),
          icon: 'fa-minimize',
          action: this.resetLaneHeights
        }
      ];

      if (INVERTIBLE_SIGNAL_TITLES.has(this.signalName?.toLowerCase())) {
        menuItems.push({
          label: t('invertPolarity'),
          icon: 'fa-solid fa-arrows-up-down',
          action: this.invertSignalPolarity
        });
      }

      menuItems.push({
        label: this.isOverflowVisible ? t('clipSignal') : t('overflowSignal'),
        icon: this.isOverflowVisible ? 'fa-solid fa-compress' : 'fa-solid fa-arrows-up-down',
        action: this.toggleSignalOverflow
      });

      return menuItems;
    },
    isSignalInverted(): boolean {
      const name = this.signalName?.toLowerCase();

      if (name === 'ecg') return Boolean(this.scoreSet?.ecgPolarityInversion);

      if (name === 'nasal_pressure') {
        return this.$store.state.epoch.graph.polarityInversion?.[this.signalName] ?? false;
      }

      return false;
    },
    isOverflowVisible(): boolean {
      return this.$store.state.epoch.graph.overflowVisible?.[this.signalName] ?? false;
    }
  },
  methods: {
    plusZoom() {
      this.adjustZoom(+1);
    },
    minusZoom() {
      this.adjustZoom(-1);
    },
    upZoom() {
      const windowSize = Math.abs(this.zoomRange[1] - this.zoomRange[0]);
      const delta = this.isSignalInverted ? windowSize * 0.25 : -windowSize * 0.25;

      const [newMin, newMax] = this.applyPanClamp(
        this.signalName,
        this.zoomRange[0] + delta,
        this.zoomRange[1] + delta
      );

      this.$store.commit('epoch/setSignalZoom', {
        signal: this.signalName,
        to: [newMin, newMax]
      });

      this.closeMenu();
    },
    downZoom() {
      const windowSize = Math.abs(this.zoomRange[1] - this.zoomRange[0]);
      const delta = this.isSignalInverted ? -windowSize * 0.25 : windowSize * 0.25;

      const [newMin, newMax] = this.applyPanClamp(
        this.signalName,
        this.zoomRange[0] + delta,
        this.zoomRange[1] + delta
      );

      this.$store.commit('epoch/setSignalZoom', {
        signal: this.signalName,
        to: [newMin, newMax]
      });

      this.closeMenu();
    },
    fitToScreenZoom() {
      if (this.actualDataMin === null || this.actualDataMax === null) return;

      const clamped = this.clampZoomToRule(
        this.signalName,
        this.actualDataMin,
        this.actualDataMax,
        true
      );

      if (!clamped) return;

      this.$store.commit('epoch/setSignalZoom', {
        signal: this.signalName,
        to: clamped
      });

      this.closeMenu();
    },
    fitAllToPane() {
      window.dispatchEvent(new CustomEvent('fit-all-to-pane', { detail: this.pane }));
      this.closeMenu();
    },
    resetHoveredToDefault() {
      const range = this.getDefaultRangeFor(this.signalName);
      if (!range) return;

      this.$store.commit('epoch/setSignalZoom', {
        signal: this.signalName,
        to: range
      });

      this.closeMenu();
    },
    resetAllToDefault() {
      const signalsList: ReorderableItem[] = this.$store.state.epoch.settings?.signalsList ?? [];
      const targetSignalNames = this.pane
        ? signalsList.filter((s) => s.panel === this.pane).map((s) => s.name)
        : Object.keys(signalAmplitudeRules);

      targetSignalNames.forEach((signalName) => {
        const range = this.getDefaultRangeFor(signalName);
        if (!range) return;

        this.$store.commit('epoch/setSignalZoom', {
          signal: signalName,
          to: range
        });
      });

      this.closeMenu();
    },
    hideSignal() {
      const settings = this.$store.state.epoch.settings;

      if (!settings?.signalsList) return;

      // clone the list to avoid mutating Vuex state directly
      const updatedSignals = settings.signalsList.map((item) => {
        if (item.name === this.signalName) {
          return { ...item, visible: false };
        }
        return item;
      });

      this.$store.commit('epoch/applySettings', {
        ...settings,
        signalsList: updatedSignals
      });

      this.closeMenu();
    },
    clampZoomToRule(signalName: string, rawMin: number, rawMax: number, allowExpand = false) {
      const rule = signalAmplitudeRules[signalName];

      let min = Math.min(rawMin, rawMax);
      let max = Math.max(rawMin, rawMax);

      if (!rule) return [min, max];

      const absMin = Number(rule.absoluteMin);
      const absMax = Number(rule.absoluteMax);

      // Always enforce absolute limits
      min = Math.max(min, absMin);
      max = Math.min(max, absMax);

      const isAuto = rule.min === 'auto' || rule.max === 'auto';

      const minWindow = rule.symmetric ? rule.step / 2 : rule.step;

      const width = max - min;

      if (width < minWindow) {
        if (isAuto) {
          return [min, max];
        }

        if (!allowExpand) return null;

        // Expand window for fit
        const center = (min + max) / 2;
        min = rule.symmetric ? center - minWindow / 2 : min;

        max = rule.symmetric ? center + minWindow / 2 : min + minWindow;

        // Re-clamp after expansion
        min = Math.max(min, absMin);
        max = Math.min(max, absMax);
      }

      return [min, max];
    },
    applyPanClamp(signalName, newMin, newMax) {
      const rule = signalAmplitudeRules[signalName];
      if (!rule) return [newMin, newMax];

      const absMin = Number(rule.absoluteMin);
      const absMax = Number(rule.absoluteMax);

      const windowSize = newMax - newMin;

      // clamp
      if (newMin < absMin) {
        newMin = absMin;
        newMax = absMin + windowSize;
      }

      if (newMax > absMax) {
        newMax = absMax;
        newMin = absMax - windowSize;
      }

      return [newMin, newMax];
    },
    adjustZoom(direction: 1 | -1) {
      const rule = signalAmplitudeRules[this.signalName];

      if (!rule) return;

      const [min, max] = this.zoomRange;
      const step = rule.symmetric ? rule.step / 2 : rule.step;

      const center = (min + max) / 2;
      const half = (max - min) / 2;

      let nextHalf;

      if (direction === 1 && half === step) {
        nextHalf = half / 2;
      } else {
        nextHalf = half - direction * step;
      }

      if (nextHalf <= 0) return;

      const minAllowedHalf = step / 2;

      if (nextHalf < minAllowedHalf) return;

      const nextMin = rule.symmetric ? center - nextHalf : min + step * direction;
      const nextMax = center + nextHalf;
      const clamped = this.clampZoomToRule(this.signalName, nextMin, nextMax);

      if (!clamped) return;

      this.$store.commit('epoch/setSignalZoom', {
        signal: this.signalName,
        to: clamped
      });

      this.closeMenu();
    },
    getDefaultRangeFor(signalName: string): [number, number] | null {
      const rule = signalAmplitudeRules[signalName];
      if (!rule) return null;

      // Auto signals (Nasal_pressure)
      if (rule.min === 'auto' || rule.max === 'auto') {
        const baseMapping = this.$store.state.epoch.epochData?.data?.mapping?.[signalName];

        if (baseMapping?.min !== undefined && baseMapping?.max !== undefined) {
          return [Number(baseMapping.min), Number(baseMapping.max)];
        }

        const autoDefaults = this.$store.state.epoch.initialAutoDefaults?.[signalName];
        return autoDefaults ? [autoDefaults.min, autoDefaults.max] : null;
      }

      // Normal signals
      return [rule.min as number, rule.max as number];
    },
    invertSignalPolarity() {
      this.$emit('invert-polarity', this.signalName);
      this.closeMenu();
    },
    toggleSignalOverflow() {
      this.$store.commit('epoch/setSignalOverflow', {
        signal: this.signalName,
        value: !this.isOverflowVisible
      });
      this.closeMenu();
    },
    applyOverflowToAll() {
      this.$store.commit('epoch/setAllSignalsOverflow', !this.isOverflowVisible);
      this.closeMenu();
    },
    resetLaneHeights() {
      this.$store.commit('epoch/resetSignalHeightsFromPreset');
      this.closeMenu();
    },
    closeMenu() {
      this.internalShow = false;
    }
  }
});
</script>

<style lang="scss" src="./chart-context-menu.style.scss" scoped></style>
